import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  TerminalParamExportDocument,
  TerminalParamRegistry,
  TerminalParamValue as ContractTerminalParamValue,
} from '@reform-lite/contracts';
import { apiClient } from '@/lib/api-client';

export type TerminalParamValue = ContractTerminalParamValue;

export type TerminalParamAssignment = {
  id: string;
  key: string;
  type: string;
  value: TerminalParamValue;
  scope: 'global' | 'profile' | 'terminal';
  scopeId: string;
  updatedAt: string;
  updatedBy: string | null;
  /**
   * Why the resolver drops this row, or null when it is delivered
   * (TP-FR-025). Optional only so older fixtures parse; the API always
   * sends it.
   */
  undelivered?: string | null;
  /** Profile name or `code · display name` of the scope (RL-375); absent for global. */
  scopeName?: string;
};

export type TerminalParamScope = 'global' | 'profile' | 'terminal';

const TERMINAL_PARAMS_KEY = ['terminal-params'] as const;
const REGISTRY_KEY = ['terminal-params', 'registry'] as const;

/**
 * The declared vocabulary the editor renders from (TP-FR-003).
 *
 * This was cached for the whole session — the registry "changes only on a
 * deploy", so once fetched it could never go stale. The missing half of that
 * sentence is that a deploy does not close the operator's tab. The backend is
 * deployed while Back Office is open; the tab keeps rendering the descriptor
 * list it fetched hours ago; and parameters that the API will happily accept
 * are simply absent from the editor, with nothing on screen suggesting the
 * list is old (RL-361). That is how a whole registry group went missing while
 * `GET /terminal-params/registry` was returning it.
 *
 * So: stale after a minute, polled every minute while the editor is mounted,
 * and refetched when the operator comes back to the tab. Staleness alone does
 * not initiate a request, so the interval is what keeps a continuously focused
 * editor current. React Query pauses interval polling in a background tab by
 * default. The list is small and the request is cheap; the cost of asking again
 * is far below the cost of an operator believing a parameter does not exist.
 */
export function useTerminalParamRegistry(enabled = true) {
  return useQuery({
    queryKey: REGISTRY_KEY,
    queryFn: async (): Promise<TerminalParamRegistry> => {
      const response = await apiClient.get<TerminalParamRegistry>('/terminal-params/registry');
      return response.data;
    },
    enabled,
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

function terminalParamsKey(scope: TerminalParamScope, scopeId: string, key?: string) {
  return [...TERMINAL_PARAMS_KEY, scope, scopeId, key ?? '*'] as const;
}

export function useTerminalParams({
  scope,
  scopeId,
  key,
  enabled = true,
}: {
  scope: TerminalParamScope;
  scopeId: string;
  key?: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: terminalParamsKey(scope, scopeId, key),
    queryFn: async (): Promise<TerminalParamAssignment[]> => {
      const response = await apiClient.get<TerminalParamAssignment[]>('/terminal-params', {
        params: { scope, scopeId, ...(key === undefined ? {} : { key }) },
      });
      return response.data;
    },
    enabled,
  });
}

/**
 * Every authored assignment in the estate, unfiltered (TP-FR-016). The editor
 * needs the whole scope chain to say which scope won and what clearing would
 * uncover, and the set is small — tens of rows — so one query beats three.
 */
export function useAllTerminalParams(enabled = true) {
  return useQuery({
    queryKey: [...TERMINAL_PARAMS_KEY, 'all'] as const,
    queryFn: async (): Promise<TerminalParamAssignment[]> => {
      const response = await apiClient.get<TerminalParamAssignment[]>('/terminal-params');
      return response.data;
    },
    enabled,
  });
}

export type TerminalParamImpact = {
  terminals: number;
  willChange: number;
  overriddenBelow: number;
};

/**
 * Blast radius of a write at one scope (TP-FR-018), computed server-side from
 * the same delivery rules the resolver applies. Asked when a row becomes
 * dirty, so the operator reads it before pressing Save.
 */
export function useTerminalParamImpact({
  key,
  scope,
  scopeId,
  enabled = true,
}: {
  key: string;
  scope: TerminalParamScope;
  scopeId: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [...TERMINAL_PARAMS_KEY, 'impact', scope, scopeId, key] as const,
    queryFn: async (): Promise<TerminalParamImpact> => {
      const response = await apiClient.get<TerminalParamImpact>('/terminal-params/impact', {
        params: { key, scope, scopeId },
      });
      return response.data;
    },
    enabled,
  });
}

export type TerminalParamHistoryEntry = {
  id: string;
  occurredAt: string;
  action: 'set' | 'clear';
  actorUserId: string | null;
  actorEmail: string | null;
  from: TerminalParamValue | null;
  to: TerminalParamValue | null;
};

/** Who changed one parameter at one scope, from the audit log (TP-FR-022). Read-only. */
export function useTerminalParamHistory({
  key,
  scope,
  scopeId,
  enabled = true,
}: {
  key: string;
  scope: TerminalParamScope;
  scopeId: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [...TERMINAL_PARAMS_KEY, 'history', scope, scopeId, key] as const,
    queryFn: async (): Promise<TerminalParamHistoryEntry[]> => {
      const response = await apiClient.get<TerminalParamHistoryEntry[]>(
        '/terminal-params/history',
        { params: { key, scope, scopeId } },
      );
      return response.data;
    },
    enabled,
  });
}

export function useSetTerminalParam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      scope: TerminalParamScope;
      scopeId: string;
      key: string;
      value: TerminalParamValue;
    }): Promise<TerminalParamAssignment> => {
      const response = await apiClient.post<TerminalParamAssignment>('/terminal-params', {
        key: input.key,
        scope: input.scope,
        scopeId: input.scopeId,
        value: input.value,
      });
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TERMINAL_PARAMS_KEY }),
  });
}

/**
 * Set a password parameter (MICRO-10). The plain password goes to its own
 * endpoint in a `password` field; Back Office keeps only its hash and answers,
 * like every read, with a placeholder. Clearing is the ordinary clear.
 */
export function useSetTerminalParamSecret() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      scope: TerminalParamScope;
      scopeId: string;
      key: string;
      password: string;
    }): Promise<TerminalParamAssignment> => {
      const response = await apiClient.post<TerminalParamAssignment>('/terminal-params/secret', {
        key: input.key,
        scope: input.scope,
        scopeId: input.scopeId,
        password: input.password,
      });
      return response.data;
    },
    // P10-2: a mutation keeps its variables — here the typed password — in
    // the mutation cache until it is garbage-collected (5 minutes by
    // default). Collect it as soon as nothing observes it; the editor also
    // resets it the moment the request settles.
    gcTime: 0,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TERMINAL_PARAMS_KEY }),
  });
}

export function useClearTerminalParam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      scope: TerminalParamScope;
      scopeId: string;
      key: string;
    }): Promise<void> => {
      await apiClient.delete('/terminal-params', { params: input });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TERMINAL_PARAMS_KEY }),
  });
}

/**
 * Download the tenant's authored assignments as a JSON file (TP-FR-028). The
 * server names the file after the tenant, the registry version and the
 * moment of export; the browser saves it under that name.
 */
export function useExportTerminalParams() {
  return useMutation({
    mutationFn: async (): Promise<{ filename: string; document: TerminalParamExportDocument }> => {
      const response = await apiClient.get<TerminalParamExportDocument>('/terminal-params/export');
      const disposition =
        (response.headers as Record<string, string | undefined>)['content-disposition'] ?? '';
      const match = /filename="([^"]+)"/.exec(disposition);
      const filename = match?.[1] ?? 'terminal-params.json';
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(url);
      return { filename, document: response.data };
    },
  });
}

// ---------- Import (TP-FR-030, TP-FR-031) ----------

export type ImportRowOutcome = 'add' | 'change' | 'unchanged' | 'skip';

export type ImportPreviewRow = {
  key: string;
  type: string;
  scope: TerminalParamScope;
  scopeId: string;
  scopeName?: string;
  value: TerminalParamValue;
  outcome: ImportRowOutcome;
  currentValue: TerminalParamValue | null;
  reason?: string;
};

export type ImportPreview = {
  source: { tenant: { id: string; slug: string }; registryVersion: number; exportedAt: string };
  targetRegistryVersion: number;
  rows: ImportPreviewRow[];
  totals: Record<ImportRowOutcome, number>;
};

export type ImportApplyResult = ImportPreview & { applied: number };
export type ImportApplyExpectation = Pick<
  ImportPreviewRow,
  'key' | 'scope' | 'scopeId' | 'value' | 'currentValue'
> & { outcome: 'add' | 'change' };

/** What importing a file would do, row by row, with nothing written. */
export function useImportPreview() {
  return useMutation({
    mutationFn: async (document: TerminalParamExportDocument): Promise<ImportPreview> => {
      const response = await apiClient.post<ImportPreview>(
        '/terminal-params/import/preview',
        document,
      );
      return response.data;
    },
  });
}

/** Apply the rows the operator kept after seeing the preview. */
export function useApplyImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      document: TerminalParamExportDocument;
      expectedRows: ImportApplyExpectation[];
    }): Promise<ImportApplyResult> => {
      const response = await apiClient.post<ImportApplyResult>(
        '/terminal-params/import/apply',
        input,
      );
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TERMINAL_PARAMS_KEY }),
  });
}

// ---------- Copy between profiles (TP-FR-027) ----------

export type CopyProfileParamsInput = {
  fromProfileId: string;
  toProfileId: string;
  keys?: string[];
  expectedRows?: ImportApplyExpectation[];
};

/** What copying one profile's authored parameters onto another would do. */
export function useCopyPreview() {
  return useMutation({
    mutationFn: async (input: CopyProfileParamsInput): Promise<ImportPreview> => {
      const response = await apiClient.post<ImportPreview>('/terminal-params/copy/preview', {
        fromProfileId: input.fromProfileId,
        toProfileId: input.toProfileId,
      });
      return response.data;
    },
  });
}

/** Apply a previewed copy, restricted to the keys the operator kept. */
export function useApplyCopy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CopyProfileParamsInput): Promise<ImportApplyResult> => {
      const response = await apiClient.post<ImportApplyResult>(
        '/terminal-params/copy/apply',
        input,
      );
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TERMINAL_PARAMS_KEY }),
  });
}
